import { useRequest } from "ahooks";
import { isEqual } from "lodash";

import { useEffect, useMemo, useRef, useState } from "react";

import LoadingButton from "@mui/lab/LoadingButton";
import { Slider, Typography } from "@mui/material";

import { setUserPreference } from "@/api/account";
import Programs from "@/components/AlertModal/Programs";
import MessageDialog from "@/components/MessageDialog";
import SearchSelect from "@/components/SearchBar/SearchSelect";
import { cabinOptionMap } from "@/constants";
import { siteName } from "@/constants/config";
import { BANK_NAME_MAP, PROGRAM_NAME_MAP, tranfer_bank_to_program } from "@/constants/programs";
import { useGlobalStoreContext } from "@/context/globalStoreContext";
import useEvent from "@/hooks/useEvent";
import { transformerOptions } from "@/utils";

import FormItem from "./FormItem";

interface Option {
  label: string;
  value: string;
}

interface FromData {
  banks: Option[];
  airlines: Option[];
  cabins: string;
  percent_premium: number;
}

const allOption = { label: "All", value: "" };

const PreferredPrograms = () => {
  const [result, setResult] = useState<{ message: string; status: 0 | 1 }>();

  const store = useGlobalStoreContext();

  const bankOptions = useMemo(() => {
    return Object.keys(BANK_NAME_MAP)
      .map((key) => ({ value: key, label: BANK_NAME_MAP[key] }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, []);

  const airlineOptions = useMemo(() => {
    return transformerOptions(PROGRAM_NAME_MAP)
      .map((option) => ({
        value: option.value,
        label: option.label,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, []);

  const defaultFormData = useRef({
    banks: [allOption],
    airlines: [allOption],
    cabins: cabinOptionMap[0].value,
    percent_premium: 50,
  });

  const [formData, setFormData] = useState<FromData>(defaultFormData.current);

  useEffect(() => {
    const banks =
      store.user_preference.cards?.map((code) => ({
        label: BANK_NAME_MAP[code],
        value: code,
      })) ?? [];

    const airlines =
      store.user_preference.programs?.map((code) => ({
        label: PROGRAM_NAME_MAP.get(code) || "",
        value: code,
      })) ?? [];

    setFormData({
      banks: banks.length === 0 ? [allOption] : banks,
      airlines: airlines.length === 0 ? [allOption] : airlines,
      cabins: store.user_preference.cabins,
      percent_premium: store.user_preference.percent_premium,
    });
  }, [store.user_preference]);

  const handleBankChange = useEvent((data: Option[]) => {
    let nextAirline = [...formData.airlines];

    if (data.length) {
      let programs: string[] = [];

      data.forEach((bank) => {
        programs = programs.concat(tranfer_bank_to_program.get(bank.label) || []);
      });

      programs = [...new Set(programs)];

      if (programs.length) {
        programs = [...programs, ...formData.airlines.map((ele) => ele.value)];
        programs = [...new Set(programs)];
        nextAirline = airlineOptions.filter((ele) => programs.includes(ele.value));
      }
    }

    const banks = data.map((item) => item.value).filter(Boolean);

    setFormData({
      ...formData,
      banks: data.length === 0 || isEqual(banks.length, bankOptions.length) ? [allOption] : data,
      airlines: nextAirline,
    });
  });

  const handleAirlineChange = useEvent((data: Option[]) => {
    const airlines = data.map((item) => item.value).filter(Boolean);

    setFormData({
      ...formData,
      airlines:
        data.length === 0 || isEqual(airlines.length, airlineOptions.length) ? [allOption] : data,
    });
  });

  const { loading, runAsync } = useRequest(
    () =>
      setUserPreference({
        programs: formData.airlines.map((item) => item.value).filter(Boolean),
        cards: formData.banks.map((item) => item.value).filter(Boolean),
        cabins: formData.cabins,
        percent_premium: formData.percent_premium,
      }),
    {
      manual: true,
    }
  );

  return (
    <div>
      <div className="mb-[20px]">
        <Typography variant="h4" className="mb-[15px]">
          Program Preference
        </Typography>
        <Typography>
          {"Save your preferred card programs or airline loyalty programs, and we will automatically apply your selections to each search you perform across all " +
            siteName +
            " products."}
        </Typography>
      </div>
      <FormItem label="Card programs:">
        <Programs
          size="small"
          title="Bank Programs"
          options={bankOptions}
          selectOptions={formData.banks}
          onChange={handleBankChange}
        />
      </FormItem>

      <FormItem label="Airline programs:">
        <Programs
          size="small"
          title="Airline Programs"
          options={airlineOptions}
          selectOptions={formData.airlines}
          onChange={handleAirlineChange}
        />
      </FormItem>
      <FormItem label="Preferred cabins:">
        <SearchSelect
          value={formData.cabins}
          options={cabinOptionMap}
          onChange={(data) => {
            setFormData({ ...formData, cabins: data });
          }}
          border
          block
        />
      </FormItem>
      <FormItem label="Preferred premium percentage(for business/first class flight):">
        <Slider
          valueLabelDisplay="on"
          getAriaValueText={(value: number) => `${value}%`}
          min={0}
          max={100}
          value={formData.percent_premium}
          onChange={(e, data) => {
            setFormData({ ...formData, percent_premium: data as number });
          }}
        />
      </FormItem>

      <div className="text-right">
        <LoadingButton
          onClick={() => {
            setFormData(defaultFormData.current);
          }}
          // loading={loading}
          // variant=""
          className="!mr-[10px]"
        >
          Reset
        </LoadingButton>
        <LoadingButton
          loading={loading}
          variant="contained"
          onClick={async () => {
            try {
              await runAsync();
              setResult({
                status: 1,
                message: "Your preference has been saved!",
              });
            } catch (error) {
              setResult({
                status: 0,
                message: "fail",
              });
            }
          }}
        >
          Save
        </LoadingButton>
      </div>

      <MessageDialog
        open={!!result}
        onOpen={() => setResult(undefined)}
        message={result?.message}
        status={result?.status}
      />
    </div>
  );
};

export default PreferredPrograms;
